/**
 * What this customer counts money in, and where that fact is kept.
 *
 * Every amount in the interface was printed with "KM" welded to it, because
 * there was one customer and they are in Bosnia. Serbia pays in dinars, Croatia
 * and Slovenia in euros — and a saving of "4.000,00 KM" shown to a factory in
 * Osijek is not a cosmetic slip. It is off by a factor of two and reads as
 * authoritative.
 *
 * Deliberately free of React, and shaped like `i18n/locale.ts` for the same
 * reasons: `lib/utils.ts` formats money outside any component, so the value has
 * to be readable without a hook, and the rules below can then be tested without
 * a DOM.
 *
 * ## Not stored, unlike the locale
 *
 * The language is a choice somebody makes on a device and it belongs in
 * `localStorage`. The currency is not a choice at all — it is a property of the
 * company, decided at onboarding, and the only correct value is whatever the
 * server last said. Keeping a copy across reloads would mean a stale one
 * outliving a correction, to save a flash that cannot happen: nothing renders
 * an amount until `/auth/me` has answered.
 */

export interface Currency {
  code: string
  /** What is printed next to the number: "KM", "€", "RSD". */
  symbol: string
  /** True only for the dollar. Everything else here trails the amount. */
  symbolLeads: boolean
}

/**
 * Bosnian marks, matching `config('app.currency')` on the server.
 *
 * Used before a session exists and for a company whose currency was never set.
 * It is the same fallback the API applies, so the two cannot disagree about
 * what an unconfigured customer sees.
 */
export const DEFAULT_CURRENCY: Currency = { code: 'BAM', symbol: 'KM', symbolLeads: false }

let activeCurrency: Currency = DEFAULT_CURRENCY

export function getActiveCurrency(): Currency {
  return activeCurrency
}

/**
 * Take the currency from a signed-in session.
 *
 * Called wherever the user is set, including on sign-out — passing nothing
 * returns to the default, so the sign-in screen of a shared terminal does not
 * keep the last person's company's currency.
 *
 * Tolerant of a partial object on purpose. This reads a field of an API
 * response, and a client built against a server that predates the field should
 * fall back rather than render `undefined` beside every amount.
 */
export function adoptCompanyCurrency(currency: Partial<Currency> | null | undefined): void {
  activeCurrency =
    currency && typeof currency.code === 'string' && typeof currency.symbol === 'string'
      ? {
          code: currency.code,
          symbol: currency.symbol,
          symbolLeads: currency.symbolLeads === true,
        }
      : DEFAULT_CURRENCY
}

/**
 * Another company's currency, from its ISO code.
 *
 * The owner console reads amounts of many customers at once, each in its own
 * currency, and gets only the code with them. The symbol comes from the list
 * `/api/meta` already serves for the currency dropdown; a code missing from it
 * prints as itself, which is unambiguous if not pretty. Only the dollar leads,
 * as `Currency.symbolLeads` says.
 */
export function currencyOf(
  code: string | null | undefined,
  known: readonly { value: string; symbol: string }[] = [],
): Currency {
  if (!code) return DEFAULT_CURRENCY

  const match = known.find((currency) => currency.value === code)

  return { code, symbol: match?.symbol ?? code, symbolLeads: code === 'USD' }
}
